import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Probá de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Sesión iniciada");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "No se pudo ingresar"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-grain grid place-items-center px-6"
    >
      <div className="aurora-blob" style={{
        width: 400, height: 400,
        background: "hsl(var(--accent-green) / 0.25)",
        top: -80, left: -80,
      }} />
      <div className="aurora-blob" style={{
        width: 320, height: 320,
        background: "hsl(var(--accent-magenta) / 0.2)",
        bottom: 40, right: -60,
        animationDelay: "3s",
      }} />

      <form
        data-testid="admin-login-form"
        onSubmit={handleSubmit}
        className="relative w-full max-w-md glass p-8 md:p-10 z-10"
      >
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent-green))] mb-3">
          // admin_login
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-2">
          Acceso restringido
        </h1>
        <p className="text-muted-foreground text-sm mb-8">
          Solo el administrador del portal puede entrar acá.
        </p>

        <div className="space-y-5">
          <div>
            <label
              htmlFor="admin-email"
              className="block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1"
            >
              email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@dvassalloapps.com.ar"
              autoComplete="email"
              required
              className="w-full bg-transparent border-b border-border focus:border-[hsl(var(--accent-cyan))] outline-none py-2.5 font-mono text-sm transition-colors"
            />
          </div>

          <div>
            <label
              htmlFor="admin-password"
              className="block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1"
            >
              password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
              className="w-full bg-transparent border-b border-border focus:border-[hsl(var(--accent-magenta))] outline-none py-2.5 font-mono text-sm transition-colors"
            />
          </div>
        </div>

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={submitting}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[hsl(var(--accent-green))] text-background font-mono font-bold uppercase tracking-[0.15em] text-xs px-6 py-3 border-2 border-[hsl(var(--accent-green))] hover:bg-transparent hover:text-[hsl(var(--accent-green))] disabled:opacity-50 transition-colors"
        >
          {submitting ? "ingresando..." : "ingresar"}
          {!submitting && <ArrowRight size={14} />}
          {submitting && <Lock size={14} />}
        </button>

        <a
          href="/"
          className="mt-6 block text-center font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="admin-back-home"
        >
          ← volver al sitio
        </a>
      </form>
    </div>
  );
}
